import { supabase } from './supabaseClient';

export type PublicProfileGatewayRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  address?: string | null;
  plz?: string | null;
  city?: string | null;
  birthday?: string | null;
  phone_number?: string | null;
  avatar_url: string | null;
  ressort: string | null;
  rolle: string | null;
  admin?: boolean | null;
  mitglied_seit?: string | null;
  is_dancer?: boolean | null;
  nickname: string | null;
};

function isMissingGateway(error: { code?: string | null; message?: string | null }) {
  const code = (error.code ?? '').trim();
  const message = (error.message ?? '').toLowerCase();
  return (
    code === 'PGRST202' ||
    message.includes('could not find the function') ||
    message.includes('schema cache')
  );
}

function normalizeRows(data: unknown): PublicProfileGatewayRow[] {
  if (!Array.isArray(data)) return [];
  return data.map((row) => row as PublicProfileGatewayRow);
}

export function sortPublicProfiles<T extends Pick<PublicProfileGatewayRow, 'first_name' | 'last_name' | 'nickname'>>(
  rows: T[],
): T[] {
  return [...rows].sort((a, b) => {
    const al = (a.last_name ?? '').trim();
    const bl = (b.last_name ?? '').trim();
    if (al !== bl) return al.localeCompare(bl, 'de');
    const af = (a.first_name ?? '').trim();
    const bf = (b.first_name ?? '').trim();
    if (af !== bf) return af.localeCompare(bf, 'de');
    return (a.nickname ?? '').trim().localeCompare((b.nickname ?? '').trim(), 'de');
  });
}

export async function fetchPublicProfiles(ids?: string[]): Promise<PublicProfileGatewayRow[]> {
  const normalizedIds = Array.from(new Set((ids ?? []).map((id) => id.trim()).filter(Boolean)));

  const callRpc = async () => {
    const { data, error } = await supabase.rpc('get_public_profiles', normalizedIds.length ? { p_ids: normalizedIds } : {});
    if (error) throw error;
    return normalizeRows(data);
  };

  try {
    return await callRpc();
  } catch (error) {
    const err = error as { code?: string | null; message?: string | null };
    if (!isMissingGateway(err)) {
      throw error;
    }
  }

  let query = supabase.from('public_profiles').select('*');
  if (normalizedIds.length) {
    query = query.in('id', normalizedIds);
  }
  const { data, error } = await query;
  if (error) throw error;
  return normalizeRows(data);
}
